import { Info } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import { cn } from '~/utils/cn';
import { formatDateTime } from '~/utils/date';
import { humanizeRoleCode } from '~/utils/humanize-role';

import type { CostActual } from '../types/cost-estimate.types';
import { formatUsd } from '../utils/format-usd';

/** i18n key per known post-step kind; unknown kinds fall back to a humanised code. */
const STEP_LABEL_KEY: Record<string, string> = {
  STEP_SCORING_PRE: 'costActual.step.scoringPre',
  STEP_SCORING_POST: 'costActual.step.scoringPost',
  STEP_TRANSLATION_POST: 'costActual.step.translationPost',
  STEP_GAP_ANALYSIS_POST: 'costActual.step.gapAnalysisPost',
};

/** The estimate the actual is measured against, pro-rated or full-run. */
interface ActualComparison {
  /** Expected spend for the products covered (null = no usable estimate → no percentage). */
  expectedUsd: number | null;
  lowUsd: number | null;
  highUsd: number | null;
  /** Actual vs expected, in percent. */
  pct: number | null;
  /** True when the estimate was scaled to `enrichedProducts / estimate.productCount`. */
  proRated: boolean;
}

/**
 * Comparison basis for the actual. While enrichment is still running the partial actual
 * is measured against the estimate pro-rated to the products processed so far (a
 * full-run comparison would read e.g. −90% at 10% progress); once done it uses the full
 * estimate and the backend's `differencePct`. No percentage when either count is 0.
 */
interface BreakdownRow {
  key: string;
  label: string;
  products: number | null;
  costUsd: number | null;
}

function compareActual(actual: CostActual, proRate: boolean): ActualComparison {
  const est = actual.estimate;
  const none = { expectedUsd: null, lowUsd: null, highUsd: null, pct: null, proRated: proRate };
  if (!est) return none;
  if (!proRate) {
    return {
      expectedUsd: est.avgUsd,
      lowUsd: est.lowUsd,
      highUsd: est.highUsd,
      pct: actual.differencePct,
      proRated: false,
    };
  }
  if (!est.productCount || !actual.enrichedProducts) return none;
  const ratio = actual.enrichedProducts / est.productCount;
  const expectedUsd = est.avgUsd * ratio;
  return {
    expectedUsd,
    lowUsd: est.lowUsd == null ? null : est.lowUsd * ratio,
    highUsd: est.highUsd == null ? null : est.highUsd * ratio,
    pct: expectedUsd > 0 ? ((actual.totalUsd - expectedUsd) / expectedUsd) * 100 : null,
    proRated: true,
  };
}

/**
 * Colour of the actual against the (possibly pro-rated) likely range: above the high end
 * is warning-toned (overspend worth a look), inside the range green, below it (or no range) neutral.
 */
function actualToneClass(actual: CostActual, cmp: ActualComparison): string {
  if (cmp.pct == null || cmp.lowUsd == null || cmp.highUsd == null) return 'text-muted-foreground';
  if (actual.totalUsd > cmp.highUsd) return 'text-warning';
  if (actual.totalUsd >= cmp.lowUsd) return 'text-success';
  return 'text-muted-foreground';
}

/** Signed percent with a real minus sign, e.g. "−4.3%" / "+12.0%". */
function formatSignedPct(pct: number): string {
  const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
  return `${sign}${Math.abs(pct).toFixed(1)}%`;
}

/**
 * Actual total, "±N% vs estimate" (pro-rated: "… for N products", or "for N of M
 * products" with `ofTotal`) in the range tone, and the partial-data hint when the run
 * predates per-step cost reporting.
 */
export function CostActualValue({
  actual,
  proRate,
  ofTotal = false,
  testId = 'cost-actual',
}: {
  actual: CostActual;
  /** Compare against the estimate pro-rated to the products processed so far. */
  proRate: boolean;
  /** Name the estimated product count too ("for N of M products"). */
  ofTotal?: boolean;
  /** E2E `data-testid` for the actual total; lines derive `${testId}-...`. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  const cmp = compareActual(actual, proRate);
  let vs: string | null = null;
  if (cmp.pct != null) {
    const pct = formatSignedPct(cmp.pct);
    if (!cmp.proRated) vs = t('costActual.vsEstimate', { pct });
    else if (ofTotal)
      vs = t('costActual.vsEstimateForOf', {
        pct,
        n: actual.enrichedProducts.toLocaleString(),
        m: (actual.estimate?.productCount ?? 0).toLocaleString(),
      });
    else vs = t('costActual.vsEstimateFor', { pct, n: actual.enrichedProducts.toLocaleString() });
  }
  return (
    <>
      <span data-testid={testId} className="text-lg font-semibold">
        {formatUsd(actual.totalUsd)}
      </span>
      {vs ? (
        <span
          data-testid={`${testId}-vs-estimate`}
          className={cn('block text-xs font-normal', actualToneClass(actual, cmp))}
        >
          {vs}
        </span>
      ) : null}
      {!actual.stepCostsRecorded ? (
        <span
          data-testid={`${testId}-partial`}
          className="text-muted-foreground block text-xs font-normal"
        >
          {t('costActual.partialShort')}
        </span>
      ) : null}
    </>
  );
}

/** "8,157 of 12,000" — products the actual covers against the estimated product count. */
export function CostActualProducts({
  actual,
  estimatedCount,
}: {
  actual: CostActual;
  estimatedCount: number;
}) {
  const { t } = useTranslation('common');
  return (
    <>
      {t('costActual.productsOf', {
        processed: actual.enrichedProducts.toLocaleString(),
        estimated: (actual.estimate?.productCount ?? estimatedCount).toLocaleString(),
      })}
    </>
  );
}

/**
 * Details-page "Estimate vs actual" body: estimate (avg + likely range) against the
 * actual total and difference, a per-component breakdown, products, and computedAt.
 */
export function CostActualBreakdown({
  actual,
  estimatedCount,
  testId = 'cost-actual-breakdown',
}: {
  actual: CostActual;
  estimatedCount: number;
  /** E2E `data-testid` root; cells, rows and notes derive `${testId}-...`. */
  testId?: string;
}) {
  const { t } = useTranslation('common');
  const est = actual.estimate;
  // Fetched once, so "still in progress" = fewer products processed than estimated.
  const proRate = !!est && actual.enrichedProducts < est.productCount;
  const cmp = compareActual(actual, proRate);
  const optionalRows: BreakdownRow[] = [
    {
      key: 'searchDescriptors',
      label: t('costActual.component.searchDescriptors'),
      products: null,
      costUsd: actual.searchDescriptorsUsd,
    },
    {
      key: 'scoring',
      label: t('costActual.component.scoring'),
      products: null,
      costUsd: actual.scoringUsd,
    },
    {
      key: 'translation',
      label: t('costActual.component.translation'),
      products: null,
      costUsd: actual.translationUsd,
    },
  ];
  const rows: (BreakdownRow & { costUsd: number })[] = [
    {
      key: 'enrichment',
      label: t('costActual.component.enrichment'),
      products: actual.enrichedProducts,
      costUsd: actual.enrichmentUsd,
    },
    ...optionalRows.filter((r): r is BreakdownRow & { costUsd: number } => r.costUsd != null),
    // `scoringUsd` above already is the SCORING ledger kind; list the remaining step kinds.
    ...actual.steps
      .filter((s) => s.kind !== 'SCORING')
      .map((s, i) => {
        const labelKey = STEP_LABEL_KEY[s.kind];
        return {
          key: `${s.kind}-${String(i)}`,
          label: labelKey ? t(labelKey) : humanizeRoleCode(s.kind.replace(/^STEP_/, '')),
          products: s.products,
          costUsd: s.costUsd,
        };
      }),
  ];

  return (
    <div data-testid={testId} className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Cell testId={`${testId}-estimated`} label={t('costActual.estimated')}>
          {est ? (
            <>
              <span data-testid={`${testId}-estimated-avg`} className="text-lg font-semibold">
                {formatUsd(est.avgUsd)}
              </span>
              {est.lowUsd != null && est.highUsd != null ? (
                <span
                  data-testid={`${testId}-estimated-range`}
                  className="text-muted-foreground block text-xs font-normal"
                >
                  {t('costEstimate.likelyRangeShort', {
                    low: formatUsd(est.lowUsd),
                    high: formatUsd(est.highUsd),
                  })}
                </span>
              ) : null}
              {cmp.proRated && cmp.expectedUsd != null ? (
                <span
                  data-testid={`${testId}-estimated-pro-rated`}
                  className="text-muted-foreground block text-xs font-normal"
                >
                  {t('costActual.proRated', {
                    expected: formatUsd(cmp.expectedUsd),
                    n: actual.enrichedProducts.toLocaleString(),
                    m: est.productCount.toLocaleString(),
                  })}
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-lg font-semibold">—</span>
          )}
        </Cell>
        <Cell testId={`${testId}-actual`} label={t('costActual.actual')}>
          <CostActualValue
            testId={`${testId}-actual-total`}
            actual={actual}
            proRate={proRate}
            ofTotal
          />
        </Cell>
        <Cell testId={`${testId}-difference`} label={t('costActual.difference')}>
          {cmp.expectedUsd != null ? (
            <span
              data-testid={`${testId}-difference-amount`}
              className={cn('text-lg font-semibold', actualToneClass(actual, cmp))}
            >
              {formatUsd(actual.totalUsd - cmp.expectedUsd)}
              {cmp.pct != null ? ` (${formatSignedPct(cmp.pct)})` : ''}
            </span>
          ) : (
            '—'
          )}
        </Cell>
        <Cell testId={`${testId}-products`} label={t('costActual.productsProcessed')}>
          <CostActualProducts actual={actual} estimatedCount={estimatedCount} />
          {actual.productsWithCost !== actual.enrichedProducts ? (
            <span
              data-testid={`${testId}-products-with-cost`}
              className="text-muted-foreground block text-xs font-normal"
            >
              {t('costActual.productsWithCost', {
                n: actual.productsWithCost.toLocaleString(),
              })}
            </span>
          ) : null}
        </Cell>
      </div>

      {!actual.stepCostsRecorded ? (
        <div
          data-testid={`${testId}-partial-note`}
          className="bg-muted text-muted-foreground flex items-start gap-2 rounded-lg px-3 py-2 text-xs"
        >
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{t('costActual.partial')}</span>
        </div>
      ) : null}

      <Table data-testid={`${testId}-table`}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('costActual.table.component')}</TableHead>
            <TableHead className="text-right">{t('costActual.table.products')}</TableHead>
            <TableHead className="text-right">{t('costActual.table.cost')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key} data-testid={`${testId}-row-${r.key}`}>
              <TableCell>{r.label}</TableCell>
              <TableCell
                data-testid={`${testId}-row-${r.key}-products`}
                className="text-right tabular-nums"
              >
                {r.products === null ? '—' : r.products.toLocaleString()}
              </TableCell>
              <TableCell
                data-testid={`${testId}-row-${r.key}-cost`}
                className="text-right tabular-nums"
              >
                {formatUsd(r.costUsd)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow data-testid={`${testId}-row-total`} className="font-semibold">
            <TableCell>{t('costActual.table.total')}</TableCell>
            <TableCell />
            <TableCell data-testid={`${testId}-row-total-cost`} className="text-right tabular-nums">
              {formatUsd(actual.totalUsd)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <p data-testid={`${testId}-computed-at`} className="text-muted-foreground text-xs">
        {t('costActual.computedAt', { at: formatDateTime(actual.computedAt) })}
      </p>
    </div>
  );
}

/** Caption over a value — same stat cell as the cost card. */
function Cell({
  label,
  children,
  testId,
}: {
  label: string;
  children: ReactNode;
  /** E2E `data-testid` for the cell; the value is `${testId}-value`. */
  testId: string;
}) {
  return (
    <div data-testid={testId} className="flex min-w-0 flex-col gap-1">
      <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {label}
      </span>
      <span
        data-testid={`${testId}-value`}
        className="text-foreground text-sm font-medium tabular-nums"
      >
        {children}
      </span>
    </div>
  );
}
